import {
  IconButton,
  Input,
  InputAdornment,
  InputProps,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Search as SearchIcon, X as ClearIcon } from 'react-feather';
import { JupiterTheme } from '../../theme/types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    input: {
      background: theme.palette.coolGrey[50],
      borderRadius: 4,
      margin: theme.spacing(0, 0),
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(0.5),
      maxWidth: 500,
      display: 'flex',
      flex: '0 1 auto',
      '& ::-webkit-input-placeholder': {
        color: theme.palette.coolGrey[600],
      },
      '& ::-moz-placeholder': {
        color: theme.palette.coolGrey[600],
      },
      '& :-ms-input-placeholder': {
        color: theme.palette.coolGrey[600],
      },
      '& :-moz-placeholder': {
        color: theme.palette.coolGrey[600],
      },
    },
    disabled: {
      opacity: 0,
      transition: '0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
    },
    underline: {
      '&:before': {
        display: 'none',
      },
    },
    focused: {
      background: theme.palette.grey[50],
      transition: '0.5s cubic-bezier(0.2, 0.8, 0.2, 1)',
      flex: '1 1 auto',
      '&:before, &:after': {
        display: 'none',
      },
    },
    icon: {
      color: theme.palette.coolGrey[600],
      height: '18px',
    },
    iconDisabled: {
      pointerEvents: 'none',
      color: theme.palette.coolGrey[200],
    },
    clearButton: {
      marginRight: -4,
      padding: 0,
      transition: '0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
      '&:hover svg': {
        color: theme.palette.red[500],
      },
    },
    searchButton: {
      marginRight: -4,
      padding: 0,
      transition: '0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
      '&:hover svg': {
        color: theme.palette.primary.main,
      },
    },
    hoverDisabled: {
      pointerEvents: 'none',
    },
  })
);

const EMPTY_TEXT = '';

interface Props {
  onSearch: (value: string) => void;
  searchOnChange?: boolean;
  searchText?: string;
  placeholder?: string;
  focused?: boolean;
  InputProps?: InputProps;
  width?: string | number;
  classes?: {
    root?: string;
  };
  // Returns a string error message if the input value is not valid
  validator?: (value?: string) => string | undefined;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const QuickSearch = React.forwardRef<HTMLElement, Props>(
  function QuickSearch(
    {
      onChange,
      onClick,
      onSearch,
      searchText,
      placeholder = 'Search',
      focused,
      InputProps,
      width,
      searchOnChange,
      validator,
      ...props
    },
    ref
  ) {
    const classes = useStyles();
    const [text, setText] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined
    );
    useEffect(() => {
      if (searchText !== text) {
        setText(searchText || '');
      }
    }, [searchText]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setText(value);
      if (onChange) {
        onChange({ ...event });
      }

      if (validator) {
        const errorMessageFromValidator = validator(value);
        setErrorMessage(errorMessageFromValidator);
        if (searchOnChange && errorMessageFromValidator === undefined) {
          onSearch(value);
        }
      } else if (searchOnChange) {
        onSearch(value);
      }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && errorMessage === undefined) {
        onSearch(text);
      }
    };

    const clear = () => {
      setText(EMPTY_TEXT);
      onSearch(EMPTY_TEXT);
    };

    const onClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      clear();
    };

    const onSearchClick = () => {
      onSearch(text);
    };
    return (
      <Input
        ref={ref}
        classes={{
          root: clsx(
            classes.input,
            focused && classes.focused,
            props.classes?.root
          ),
          underline: classes.underline,
          focused: classes.focused,
        }}
        style={width ? { width } : undefined}
        type={'text'}
        fullWidth={true}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        value={text}
        onClick={onClick}
        endAdornment={
          <InputAdornment position="end">
            <Tooltip title="Clear search" enterDelay={500}>
              <span className={!text ? classes.hoverDisabled : undefined}>
                <IconButton
                  data-testid="clear-search-icon-button"
                  classes={{
                    root: classes.clearButton,
                    disabled: classes.disabled,
                  }}
                  disabled={!text}
                  onClick={onClear}
                >
                  <ClearIcon className={classes.icon} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Search" enterDelay={500}>
              <span className={!text ? classes.hoverDisabled : undefined}>
                <IconButton
                  data-testid="search-icon-button"
                  classes={{
                    root: classes.searchButton,
                    disabled: classes.disabled,
                  }}
                  disabled={!text || errorMessage !== undefined}
                  onClick={onSearchClick}
                >
                  <SearchIcon className={classes.icon} />
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        }
        {...InputProps}
      />
    );
  }
);

export default QuickSearch;
